import { Chip, ExtendedFAB, Icon, LoadingInner, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import { TDeliveryMethod } from '../../generated/graphql';
import { EMPLOYEE_PATH } from '../../routes/paths';

import { downloadFile, json2csv, readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import type { TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
// import { getReadableDependant } from '../../entities/dependants/shared';
import { employeeExportRows, employeeXlsx } from '../../entities/employees/export';
import { TEmployeeType, useQueryAllEmployees } from '../../entities/employees/queries';
import { useInfiniteScrollQuery } from '../../hooks/InitinityScroll';
import styles from './Employees.module.scss';

export const EmployeesNEW = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const navigate = useNavigate();

  const [companyFilter, setCompanyFilter] = useState<string>();
  const [employeeNameFilter, setEmployeeNameFilter] = useState<string>();
  const [selectedRows, setSelectedRows] = useState<string[]>();
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const tableRef = createRef<TTablePageRef>();

  const { data: employees, error, loading, fetchMore, nextToken } = useQueryAllEmployees();

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { sentinelRef } = useInfiniteScrollQuery({
    rootRef: tableContainerRef,
    loading,
    queries: [
      {
        fetchMore,
        nextToken
      }
    ]
  });

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(employees.map((e) => e?.company)), 'id');
  }, [employees]);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [
      {
        type: 'singleSelect',
        label: 'Company',
        name: 'companyId',
        onChange: (v) => {
          setCompanyFilter(v != null ? (v as string) : undefined);
        },
        options: companies?.map((c) => ({ label: c.name, value: c.id })),
        value: companyFilter
      },
      {
        type: 'textInput',
        label: 'Employee Name',
        name: 'employeeName',
        onChange: (v) => {
          setEmployeeNameFilter(v != null ? (v as string) : undefined);
        },
        value: employeeNameFilter
      }
    ];

    return filtersList;
  }, [companies, companyFilter, employeeNameFilter]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((e) => {
      if (e == null) return false;
      if (companyFilter != null && e?.companyId !== companyFilter) return false;

      if (employeeNameFilter != null && employeeNameFilter !== '' && (e?.fullName == null || quickScore(e?.fullName, employeeNameFilter) < 0.7)) return false;

      return true;
    });
  }, [companyFilter, employeeNameFilter, employees]);

  //console.log('employees NEW?:', employees);
  console.log('filteredEmployees NEW?:', filteredEmployees);

  const companyFilterName = useMemo(() => {
    if (!companyFilter) return undefined;
    return companies.find((c) => c.id === companyFilter)?.normalizedName || companies.find((c) => c.id === companyFilter)?.name;
  }, [companies, companyFilter]);

  const csvDownload = useCallback(() => {
    setExportLoading(true);
    downloadFile({
      data: [json2csv(employeeExportRows(filteredEmployees))],
      filename: `${companyFilterName ? `${companyFilterName}_` : ''}employees_${readableDate(new Date(), 'yyyy-MM-dd')}.csv`,
      filetype: `text/plain`
    });
    //tableRef.current.resetConfirmationModal();
    setExportLoading(false);
    setSelectedRows(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEmployees, selectedRows, companyFilterName, tableRef, tableRef.current]);

  const xlsxDownload = useCallback(async () => {
    setExportLoading(true);
    try {
      const data = await employeeXlsx(filteredEmployees);
      downloadFile({
        data: [new Uint8Array(data)],
        filename: `${companyFilterName ? `${companyFilterName}_` : ''}employees_${readableDate(new Date(), 'yyyy-MM-dd')}.xlsx`,
        filetype: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      setSelectedRows(undefined);
    } finally {
      setExportLoading(false);
    }
  }, [filteredEmployees, companyFilterName]);

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((employee: TEmployeeType) => {
    return employee.id;
  }, []);

  const onRowClick = useCallback(
    (employee: TEmployeeType) => {
      navigate(generatePath(`${EMPLOYEE_PATH}`, { employeeId: employee.id }));
    },
    [navigate]
  );

  return (
    <TablePage error={error} title='Employees (NEW)'>
      <Filters filters={filters} />
      <div ref={tableContainerRef} style={{ overflow: 'auto' }}>
        <TableInner<TEmployeeType>
          enableSorting={true}
          defaultSorting={{ id: 'fullName', desc: false }}
          data={filteredEmployees}
          emptyText={employees.length === 0 ? 'No employees registered.' : 'No employees for the current filters.'}
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          getRowId={getRowId}
          hideSubRowsSelect={true}
          hoverableRows={true}
          selectableRows={true}
          onRowClick={(row) => {
            onRowClick(row.original);
          }}
          onRowSelectionChange={onRowSelectionChange}
          virtual={false}
          columns={[
            {
              header: 'Company',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return <div className={clsx(tableClassNames.primary)}> {cell.row.original.company?.name || '-'}</div>;
              }
            },
            {
              header: 'External ID',
              accessorKey: 'externalId',
              cell: ({ cell }) => {
                return <div className={clsx(tableClassNames.tertiary)}> {cell.row.original.externalId || '-'}</div>;
              }
            },
            {
              header: 'Full Name',
              accessorKey: 'fullName',
              cell: ({ cell }) => {
                const { address, birthDate, company: { deliveryMethod, employeeBirthdayActivated } = {}, hireDate, isPaused, pausePeriod } = cell.row.original;
                let text = '';
                if (pausePeriod) {
                  const { fromDate, toDate } = pausePeriod;
                  if (fromDate) text += ` since ${readableDate(fromDate)}`;
                  if (toDate) text += ` to ${readableDate(toDate)}`;
                }
                return (
                  <div className={clsx(styles.rowNameColumn)}>
                    {isPaused && (
                      <Tooltip message={`Employee paused${text}.`}>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.fullName || `${cell.row.original.firstName ?? cell.row.original.preferredFirstName} ${cell.row.original.lastName ?? ''}`}</span>
                    {!isAddressValid(address) && deliveryMethod === TDeliveryMethod.Home && <Chip className={styleClassNames.errorChip} label='Missing address data' readonly={true} selected={true} variant='suggestion' />}
                    {(hireDate == null || hireDate === '') && <Chip className={styleClassNames.errorChip} label='Missing hire date' readonly={true} selected={true} variant='suggestion' />}
                    {(birthDate == null || birthDate === '') && employeeBirthdayActivated && <Chip className={styleClassNames.errorChip} label='Missing birth date' readonly={true} selected={true} variant='suggestion' />}
                  </div>
                );
              }
            },
            {
              header: 'Email',
              accessorKey: 'email',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span> {cell.row.original.email || '-'} </span>
                  </div>
                );
              }
            },
            /*
            {
              id: 'dependants',
              header: 'Dependants',
              accessorFn: (employee) => _.compact(employee.dependants?.items).filter((dependant) => !dependant._deleted).map(getReadableDependant).join('; ') || '',
              cell: ({ cell }) => {
                if (error) return 'Unable to load dependants';
                const items = _.compact(cell.row.original.dependants?.items).filter((dependant) => !dependant._deleted);

                return items.length > 0 ? (
                  <div className={tableClassNames.primary}>
                    {items.map((dependant) => (
                      <div key={dependant.id}>{getReadableDependant(dependant)}</div>
                    ))}
                  </div>
                ) : (
                  'None'
                );
              }
            },
            */
            {
              header: 'Hire Date',
              accessorKey: 'hireDate',
              cell: ({ cell }) => {
                if (cell.row.original.hireDate == null) return '-';
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.hireDate)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Birth Date',
              accessorKey: 'birthDate',
              cell: ({ cell }) => {
                if (cell.row.original.birthDate == null) return '-';
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.birthDate)}</span>
                  </div>
                );
              }
            }
          ]}
        />
        {loading && <LoadingInner className={clsx(tableStyles.contentLoader)} />}
        {nextToken && <div ref={sentinelRef} style={{ height: '20px' }} />}
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB
            disabled={exportLoading || !!error}
            label={`Download ${selectedRows.length} employee${selectedRows.length > 1 ? 's' : ''} to CSV`}
            leadingIcon='download'
            loading={exportLoading}
            onClick={csvDownload}
            variant='surface'
          />
          <ExtendedFAB
            disabled={exportLoading || !!error}
            label={`Download ${selectedRows.length} employee${selectedRows.length > 1 ? 's' : ''} to XLSX`}
            leadingIcon='download'
            loading={exportLoading}
            onClick={xlsxDownload}
            variant='surface'
          />
        </FloatingActions>
      )}
    </TablePage>
  );
};
